import { env } from "@/constants/env";
import { getMetaAttributionContext } from "@/utils/meta-app-events";
import { Capacitor } from "@capacitor/core";
import { Purchases } from "@revenuecat/purchases-capacitor";

// Serialize customer logins only. Advertising preparation must never hold login.
// These attribution values identify this device/app installation, not a customer.
let customerOperations: Promise<unknown> = Promise.resolve();
let customerGeneration = 0;
let pending: { generation: number; promise: Promise<void> } | undefined;

function serialize<T>(operation: () => Promise<T>): Promise<T> {
  const result = customerOperations.then(operation);
  customerOperations = result.catch(() => undefined);
  return result;
}

export function withRevenueCatCustomerChange<T>(operation: () => Promise<T>): Promise<T> {
  customerGeneration += 1;
  return serialize(operation);
}

/** Best effort, at most two seconds for the caller; no synthetic purchase events. */
export function refreshRevenueCatMetaAttribution(): Promise<void> {
  if (
    Capacitor.getPlatform() !== "ios" ||
    !["production", "staging"].includes(env.VITE_ENVIRONMENT)
  ) {
    return Promise.resolve();
  }
  const generation = customerGeneration;
  if (pending?.generation === generation) return pending.promise;

  let expired = false;
  let timer: ReturnType<typeof setTimeout>;
  const current = () => !expired && generation === customerGeneration;
  const work = (async () => {
    const context = await getMetaAttributionContext();
    if (!context || !current()) return;
    const expectedAppId =
      env.VITE_ENVIRONMENT === "staging" ? "1791204995418855" : "1054047237042045";
    if (context.appId !== expectedAppId) return;
    // The native RevenueCat SDK reads ATT and refreshes IDFA/IDFV itself.
    // Refresh replaces a previously collected IDFA with the OS's unavailable
    // value after revocation; RevenueCat treats nil/zeroed IDs as missing.
    await Purchases.collectDeviceIdentifiers();
    if (!current()) return;
    await Purchases.setFBAnonymousID({ fbAnonymousID: context.anonymousId! });
  })().catch(() => {
    console.warn("[Meta] RevenueCat attribution preparation failed");
  });
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      expired = true;
      console.warn("[Meta] RevenueCat attribution preparation timed out");
      resolve();
    }, 2_000);
  });
  const promise = Promise.race([work, timeout]).finally(() => {
    expired = true;
    clearTimeout(timer);
    if (pending?.promise === promise) pending = undefined;
  });
  pending = { generation, promise };
  return promise;
}
